// src/data/requestInternalNotes.ts
// --------------------------------
// Repository for the `request_internal_notes` table (db-122) — staff-only notes
// on a request (e.g. the reason given when assigning/releasing its tasks). RLS
// denies shelters at the ROW level, so this data can never reach a DFV Service
// even via a client bug. Typed loosely (cast) until `npm run gen:types` adds the
// table to the generated schema — mirrors the auditLogs repo pattern.

import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type InternalNoteRow = {
  id: number;
  request_id: number;
  author: string;
  author_id: string | null;
  text: string;
  kind: string;
  created_at: string;
};

type LooseClient = {
  from: (t: string) => {
    insert: (row: unknown) => Promise<{ error: { message: string } | null }>;
    select: (cols: string) => {
      eq: (
        c: string,
        v: unknown
      ) => {
        order: (
          c: string,
          o: { ascending: boolean }
        ) => Promise<{ data: unknown; error: { message: string } | null }>;
      };
    };
  };
};

// Append an internal note to a request. Silent on error (never blocks the action
// that triggered it — the assignment still succeeds even if the note write fails).
export async function insertInternalNote(note: {
  requestId: number | string;
  author: string;
  authorId?: string | null;
  text: string;
  kind?: string;
}): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient() as unknown as LooseClient | null;
  if (!c) return { error: null };
  const { error } = await c.from("request_internal_notes").insert({
    request_id: note.requestId,
    author: note.author,
    author_id: note.authorId ?? null,
    text: note.text,
    kind: note.kind ?? "note",
  });
  if (error) logger.warn("insertInternalNote failed:", error.message);
  return { error };
}

// All internal notes for a request, oldest-first (so they interleave cleanly with
// the shelter comment thread by timestamp). Shelters get an empty list via RLS.
// null on error / no client.
export async function listInternalNotesByRequest(
  requestId: number | string
): Promise<InternalNoteRow[] | null> {
  const c = getTypedClient() as unknown as LooseClient | null;
  if (!c) return null;
  const { data, error } = await c
    .from("request_internal_notes")
    .select("id, request_id, author, author_id, text, kind, created_at")
    .eq("request_id", requestId)
    .order("created_at", { ascending: true });
  if (error) {
    logger.warn("listInternalNotesByRequest failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as InternalNoteRow[];
}
